/**
 * Geometric stroke letterforms for the Alien fragment-assembly title.
 *
 * Hand-authored rather than taken from a font because the reveal needs the
 * letters decomposed into individual strokes — the effect depends on fragments
 * being sub-letter, so the word resolves in perception before the animation
 * ends. Opacity-staggering real characters just reads as a fade-in.
 *
 * Each glyph is drawn in a 100 x 140 box, origin top-left, y increasing down.
 */

type Point = [number, number];
export type Stroke = [Point, Point];

const GLYPH_WIDTH = 100;
const GLYPH_HEIGHT = 140;
/** Half the rendered stroke width; see the note on E and G below. */
const INSET = 4;

/** Only the letters "WAYNE WANG" needs. All straight lines except G's corners. */
const GLYPHS: Record<string, Stroke[]> = {
  W: [
    [[0, 0], [22, GLYPH_HEIGHT]],
    [[22, GLYPH_HEIGHT], [50, 40]],
    [[50, 40], [78, GLYPH_HEIGHT]],
    [[78, GLYPH_HEIGHT], [100, 0]],
  ],
  A: [
    [[50, 0], [5, GLYPH_HEIGHT]],
    [[50, 0], [95, GLYPH_HEIGHT]],
    [[22, 95], [78, 95]],
  ],
  Y: [
    [[5, 0], [50, 70]],
    [[95, 0], [50, 70]],
    [[50, 70], [50, GLYPH_HEIGHT]],
  ],
  N: [
    [[5, GLYPH_HEIGHT], [5, 0]],
    [[5, 0], [95, GLYPH_HEIGHT]],
    [[95, GLYPH_HEIGHT], [95, 0]],
  ],
  /*
   * E and G are inset by half the stroke width on every extreme edge.
   *
   * Strokes are centred on their path with butt caps, so a stroke lying
   * exactly on y=0 straddles it and overhangs by half its width. These are the
   * only two glyphs whose outer edges are horizontal strokes and verticals —
   * everything else terminates in a diagonal, which overhangs far less. Left
   * uncompensated they render visibly larger than their neighbours.
   */
  E: [
    [[95, INSET], [10, INSET]],
    [[10, INSET], [10, GLYPH_HEIGHT - INSET]],
    [[10, GLYPH_HEIGHT - INSET], [95, GLYPH_HEIGHT - INSET]],
    [[10, 70], [70, 70]],
  ],
  // Squared-off G with chamfered corners — geometric, no true curve.
  G: [
    [[95, INSET], [22, INSET]],
    [[22, INSET], [INSET, 22]],
    [[INSET, 22], [INSET, 118]],
    [[INSET, 118], [22, GLYPH_HEIGHT - INSET]],
    [[22, GLYPH_HEIGHT - INSET], [100 - INSET, GLYPH_HEIGHT - INSET]],
    [[100 - INSET, GLYPH_HEIGHT - INSET], [100 - INSET, 75]],
    [[100 - INSET, 75], [55, 75]],
  ],
};

/** Wide tracking is central to the look — the film's title spans the frame. */
const TRACKING = 62;
const SPACE_ADVANCE = 92;

export type Fragment = { x1: number; y1: number; x2: number; y2: number };

const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * Splits a stroke into sub-segments. Subdivision is what makes the reveal read
 * as scattered debris rather than as letters drawing themselves in.
 */
const subdivide = (stroke: Stroke, offsetX: number): Fragment[] => {
  const [[x1, y1], [x2, y2]] = stroke;
  const length = Math.hypot(x2 - x1, y2 - y1);
  // Fewer, longer pieces: heavy subdivision read as scattered sticks rather
  // than a letterform coming apart.
  const parts = Math.max(1, Math.min(2, Math.round(length / 78)));

  return Array.from({ length: parts }, (_, i) => {
    const t0 = i / parts;
    const t1 = (i + 1) / parts;
    // A small gap between pieces keeps them reading as separate fragments.
    const inset = 0.04;
    return {
      x1: lerp(x1, x2, lerp(t0, t1, inset)) + offsetX,
      y1: lerp(y1, y2, lerp(t0, t1, inset)),
      x2: lerp(x1, x2, lerp(t0, t1, 1 - inset)) + offsetX,
      y2: lerp(y1, y2, lerp(t0, t1, 1 - inset)),
    };
  });
};

export type LayoutResult = { fragments: Fragment[]; width: number };

/** Lays a string out into scattered stroke fragments plus the total advance. */
export const layoutFragments = (text: string): LayoutResult => {
  const fragments: Fragment[] = [];
  let cursor = 0;

  for (const raw of text.toUpperCase()) {
    if (raw === " ") {
      cursor += SPACE_ADVANCE;
      continue;
    }
    const strokes = GLYPHS[raw];
    if (!strokes) continue;

    for (const stroke of strokes) {
      fragments.push(...subdivide(stroke, cursor));
    }
    cursor += GLYPH_WIDTH + TRACKING;
  }

  return { fragments, width: Math.max(0, cursor - TRACKING) };
};

export const GLYPH_BOX_HEIGHT = GLYPH_HEIGHT;
